import { useEffect } from 'react';
import { useChatPanel } from '../context/ChatPanelContext';

/**
 * Routes that want the full viewport width, so the chat panel starts collapsed when
 * the user arrives on one.
 *
 * Declared here rather than as a prop on each page because the panel is mounted in
 * ChatShell, above the router — a page cannot reach it, and threading a flag down
 * through every layout to say "no chat please" would touch far more code than a list.
 *
 * A route matches itself and its descendants, so `/ivt/dashboard` also covers a future
 * `/ivt/dashboard/<industry>` without a second entry.
 */
export const FULL_WIDTH_ROUTES: readonly string[] = [
  // IVT Leader Home (GM view). The design has no chat column, and an open panel
  // squeezes the KPI band's 42px numerals and the industry matrix.
  '/ivt/dashboard',
  // IVT Impact. Already wanted this (user-requested 2026-08-03) and had its own copy of
  // the effect inside NewUseCasesPage; moved here so one policy has one implementation.
  '/ivt/impact',
];

/**
 * Which of {@link FULL_WIDTH_ROUTES} this pathname belongs to, or null.
 *
 * Returns the route rather than a boolean because the route IS the identity the collapse
 * keys on — see {@link useCollapseChatOnArrival}. A boolean cannot distinguish
 * "still on the same full-width page" from "arrived at a different one".
 *
 * Lower-cased before comparing because React Router matches routes case-insensitively:
 * `/ivt/DASHBOARD` renders the dashboard, so it has to be recognised as one.
 */
export function fullWidthRouteOf(pathname: string): string | null {
  const p = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return FULL_WIDTH_ROUTES.find((route) => p === route || p.startsWith(`${route}/`)) ?? null;
}

/**
 * Collapse the chat panel when the user ARRIVES on a full-width surface, then leave it
 * alone.
 *
 * ⭐ The dependency is the ROUTE, and both halves of that matter:
 *
 *  - NOT `chatCollapsed`. Depending on it would re-run the moment the user opened the
 *    panel and slam it shut again, making chat unreachable on the page. A panel the user
 *    opens stays open while they are there — collapse on arrival, not held.
 *  - NOT a boolean either. This hook lives in ChatShell, ABOVE the router, so it does not
 *    remount per route: with a boolean dependency `/ivt/dashboard` -> `/ivt/impact` stays
 *    `true` throughout and the second arrival would never collapse. Keying on the matched
 *    route makes each distinct full-width arrival fire, while moving within one route's
 *    descendants (`/ivt/dashboard/FSI`) keeps the key and leaves an opened panel alone.
 *
 * Each of those is a one-token edit to the dep array and each looks like a correctness
 * fix, which is why the policy is worth a named hook with tests. It previously lived as an
 * untested inline effect in NewUseCasesPage, whose per-page mount gave it the arrival
 * semantics for free — the shape this replaces, not a hypothetical one.
 *
 * ⚠️ Do NOT list a route here whose page also OPENS the panel on arrival — StorylineDetail,
 * AssetDetailHeader and UseCaseDetail each do `if (chatCollapsed) toggleChat()` on mount,
 * which is the exact opposite, and the two would fight over one piece of global state.
 *
 * ⚠️ `chatCollapsed` is app-wide, so this leaves chat collapsed on whatever page the
 * user opens next. Chosen over restoring the previous value on exit, which would fight a
 * user who had already collapsed it themselves.
 *
 * @param route the full-width route the user is on, or null. See {@link fullWidthRouteOf}.
 */
export function useCollapseChatOnArrival(route: string | null) {
  const { chatCollapsed, toggleChat } = useChatPanel();

  useEffect(() => {
    if (route && !chatCollapsed) toggleChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);
}
